/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useContext, useEffect } from 'react';
import { Text, IconButton, TextButton, Headline, Popover } from '@oneaudi/unified-web-components';
import { useI18n } from '@oneaudi/i18n-context';
import styled from 'styled-components';
import AppLoaderErrorBoundary from '../AppLoaderErrorBoundary/AppLoaderErrorBoundary';
import type { AudiWishlistProps } from '../../interfaces/header-components.interfaces';
import { wishlist } from '../../i18n/wishlist';
import { HeaderStyledLogin } from '../AudiHeader/AudiHeaderStyles';
import { logoutClickOnWishlist, loginClickOnWishlist } from '../../utils/tracking';
import { useAuth } from '../../hooks/use-auth';
import { ServiceContext } from '../../context/ServiceContext';
import useToggle from '../../hooks/use-toggle';
import { isAbsoluteUrl } from '../../utils/is-absolute-url';
import { NAVIGATION_CLASSNAME } from '../../utils/globals';
import {
  createResetActiveItemAction,
  createSetActiveItemAction,
} from '../../actions/header-actions';

const StyledContent = styled.div`
  h4 {
    margin-bottom: ${({ theme }): string => theme.spacing.s};
  }

  p {
    margin-bottom: ${({ theme }): string => theme.spacing.l};
  }

  button {
    width: 100%;
    margin-top: ${({ theme }): string => theme.spacing.m};
  }

  @media (min-width: 768px) {
    max-width: 328px;
  }
`;

const AudiWishlist: React.FC<AudiWishlistProps> = ({
  headerDispatch,
  headerNavigationItemsAmount,
  isOpen,
  useUserMenu,
  wishlistUrl,
  homeUrl,
  onClickWishlist,
}) => {
  const { authService, trackingService, headerStateService } = useContext(ServiceContext);
  const wishlistHeadlineLabel = useI18n(wishlist.wishlistHeadline);
  const wishlistSubHeadlineLabel = useI18n(wishlist.wishlistSubHeadline);
  const wishlistLoginLabel = useI18n(wishlist.wishlistLogin);
  const wishlistIconAriaLabel = useI18n(wishlist.wishlistIconAriaLabel);
  const { isAuthenticated } = useAuth();
  const [toggleElement, toggle] = useToggle();
  const isLegacy = process.env.IS_LEGACY;

  const handleLoginClick = (event: React.MouseEvent) => {
    event.preventDefault();
    const targetUrl = wishlistUrl !== 'undefined' && wishlistUrl !== '' ? wishlistUrl : homeUrl;
    authService.login(undefined, typeof window !== 'undefined' ? targetUrl : homeUrl);
  };

  const trackLogoutClick = (event: React.MouseEvent) => {
    event.preventDefault();
    const dynamicClickWishlist = logoutClickOnWishlist(headerNavigationItemsAmount + 1);

    trackingService.track(dynamicClickWishlist);
  };

  const trackLoginClick = (event: React.MouseEvent) => {
    event.preventDefault();

    const targetUrl =
      wishlistUrl !== 'undefined' && wishlistUrl !== ''
        ? wishlistUrl
        : 'https://pre-www.audi.de/de/brand/de/wishlist.html';
    const dynamicClickWishlist = loginClickOnWishlist(targetUrl, headerNavigationItemsAmount + 1);

    trackingService.track(dynamicClickWishlist);
  };

  const trackingEventClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (isAuthenticated) {
      trackLoginClick(event);
      handleLoginClick(event);
    } else {
      trackLogoutClick(event);
    }
    onClickWishlist();
  };

  useEffect(() => {
    if (toggleElement) {
      headerDispatch(
        createSetActiveItemAction({
          anchor: null,
          headerStateService,
          index: -1,
          showSearch: false,
          showDealer: false,
          showDealerSearch: false,
          showLoginFlyout: false,
          showWistListFlyout: true,
          openDealerDetails: false,
        }),
      );
    } else {
      headerDispatch(createResetActiveItemAction(headerStateService));
    }
  }, [toggleElement]);

  const renderLogOutState = (): JSX.Element => {
    return (
      <Popover
        content={
          <StyledContent>
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
            <div role="region" tabIndex={0}>
              <Headline
                tag="h4"
                variant={{
                  order: '4',
                  type: 'headline',
                  weight: 'normal',
                }}
              >
                {wishlistHeadlineLabel}
              </Headline>
              <Text
                tag="p"
                variant={{
                  order: '1',
                  style: 'normal',
                  type: 'copy',
                }}
              >
                {wishlistSubHeadlineLabel}
              </Text>
            </div>

            <TextButton aria-label="myAudi Login" onClick={handleLoginClick} variant="primary">
              {wishlistLoginLabel}
            </TextButton>
          </StyledContent>
        }
        id="whistlist-popover"
        onClose={() => {
          toggle(false);
        }}
        placement="bottom"
        alignment="right"
        role="tabpanel"
        triggerElement={
          <IconButton
            aria-label={wishlistIconAriaLabel}
            title={wishlistIconAriaLabel}
            icon="favorite"
            id="whistlist-popover"
            aria-labelledby="whistlist-popover"
            onClick={(e) => {
              toggle();
              // initiate tracking
              trackingEventClick(e);
            }}
            size="sm"
            variant="tertiary"
            className={NAVIGATION_CLASSNAME}
            // @ts-expect-error
            selected={toggleElement}
          />
        }
        withTip={isLegacy ? true : false}
        open={toggleElement}
      />
    );
  };

  const renderLogInState = () => {
    return (
      <IconButton
        variant="tertiary"
        aria-label="Wishlist"
        icon="favorite"
        size="sm"
        type="button"
        onClick={trackingEventClick}
        className={NAVIGATION_CLASSNAME}
      />
    );
  };

  return (
    // @ts-ignore
    <AppLoaderErrorBoundary>
      <HeaderStyledLogin headerNavigationItemsAmount={headerNavigationItemsAmount} isOpen={isOpen}>
        {isAbsoluteUrl(wishlistUrl) && useUserMenu
          ? isAuthenticated
            ? renderLogInState()
            : renderLogOutState()
          : null}
      </HeaderStyledLogin>
    </AppLoaderErrorBoundary>
  );
};

export default AudiWishlist;
